import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "../../../common/api/apiClient.js";
import type {
  CreateIssueRequest,
  CreateProjectRequest,
  CreateShareRequest,
  MoveIssueRequest,
  TaskBoardDTO,
  TaskIssueDTO,
  TaskProjectDTO,
  TaskProjectSummaryDTO,
  ShareDTO,
  UpdateIssueRequest,
  UpdateProjectConfigurationRequest,
} from "../../../common/api/types.js";

export const taskProjectsKey = ["tasks", "projects"] as const;
export const taskBoardKey = (projectId: string) => ["tasks", "project", projectId] as const;
export const taskSharesKey = (projectId: string) => ["tasks", "shares", projectId] as const;

export function useTaskProjects() {
  return useQuery({ queryKey: taskProjectsKey, queryFn: () => apiRequest<TaskProjectSummaryDTO[]>("/tasks/projects") });
}

export function useTaskBoard(projectId: string | null) {
  return useQuery({ queryKey: taskBoardKey(projectId ?? ""), queryFn: () => apiRequest<TaskBoardDTO>(`/tasks/projects/${projectId as string}`), enabled: projectId !== null });
}

export function useTaskShares(projectId: string, enabled: boolean) {
  return useQuery({ queryKey: taskSharesKey(projectId), queryFn: () => apiRequest<ShareDTO[]>(`/tasks/projects/${projectId}/shares`), enabled });
}

export function useShareTaskProject(projectId: string) {
  const client = useQueryClient();
  return useMutation<ShareDTO, Error, CreateShareRequest>({
    mutationFn: (body) => apiRequest<ShareDTO>(`/tasks/projects/${projectId}/shares`, { method: "POST", body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: taskSharesKey(projectId) }),
  });
}

export function useRevokeTaskShare(projectId: string) {
  const client = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (shareId) => apiRequest<void>(`/tasks/projects/${projectId}/shares/${shareId}`, { method: "DELETE" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: taskSharesKey(projectId) }),
  });
}

export function useCreateProject() {
  const client = useQueryClient();
  return useMutation<TaskProjectDTO, Error, CreateProjectRequest>({
    mutationFn: (body) => apiRequest<TaskProjectDTO>("/tasks/projects", { method: "POST", body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: taskProjectsKey }),
  });
}

export function useCreateIssue(projectId: string) {
  const client = useQueryClient();
  return useMutation<TaskIssueDTO, Error, CreateIssueRequest>({
    mutationFn: (body) => apiRequest<TaskIssueDTO>(`/tasks/projects/${projectId}/issues`, { method: "POST", body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: taskBoardKey(projectId) }),
  });
}

export function useUpdateIssue(projectId: string) {
  const client = useQueryClient();
  return useMutation<TaskIssueDTO, Error, { issueId: string; body: UpdateIssueRequest }>({
    mutationFn: ({ issueId, body }) => apiRequest<TaskIssueDTO>(`/tasks/projects/${projectId}/issues/${issueId}`, { method: "PUT", body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: taskBoardKey(projectId) }),
  });
}

export function useMoveIssue(projectId: string) {
  const client = useQueryClient();
  return useMutation<TaskIssueDTO, Error, { issueId: string; body: MoveIssueRequest }>({
    mutationFn: ({ issueId, body }) => apiRequest<TaskIssueDTO>(`/tasks/projects/${projectId}/issues/${issueId}/move`, { method: "POST", body }),
    onSuccess: () => void client.invalidateQueries({ queryKey: taskBoardKey(projectId) }),
  });
}

export function useRunIssueAction(projectId: string) {
  const client = useQueryClient();
  return useMutation<TaskIssueDTO, Error, { issueId: string; actionId: string }>({
    mutationFn: ({ issueId, actionId }) => apiRequest<TaskIssueDTO>(`/tasks/projects/${projectId}/issues/${issueId}/actions/${actionId}`, { method: "POST" }),
    onSuccess: () => void client.invalidateQueries({ queryKey: taskBoardKey(projectId) }),
  });
}

export function useConfigureProject(projectId: string) {
  const client = useQueryClient();
  return useMutation<TaskProjectDTO, Error, UpdateProjectConfigurationRequest>({
    mutationFn: (body) => apiRequest<TaskProjectDTO>(`/tasks/projects/${projectId}/configuration`, { method: "PUT", body }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: taskBoardKey(projectId) });
      void client.invalidateQueries({ queryKey: taskProjectsKey });
    },
  });
}
